import Combobox from "@/components/Combobox";
import InputError from "@/components/InputError";
import Modal from "@/components/Modal";
import SkeletonRow from "@/components/SkeletonRow";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  listHubunganKeluarga,
  listKebutuhanKhusus,
  listSizeOptions,
} from "@/constants";
import useFilePreview from "@/hooks/useFilePreview";
import useSessionStorage from "@/hooks/useSessionStorage";
import { useUser } from "@/hooks/useUser";
import { formatDateIndonesia } from "@/lib/utils";
import {
  getDataSiswaLengkap,
  getListAgama,
  getListAlatTransportasi,
  getListJenisTinggal,
  getListNegara,
  getListSekolah,
  getListWilayah,
} from "@/services/getData";
import {
  Agama,
  AlatTransportasi,
  BiodataSate,
  DataPendaftaran,
  DataPilihanProdi,
  DataSiswa,
  ErrorType,
  HubunganKeluargaEnum,
  JenisTinggal,
  LokasiKuliah,
  Negara,
  Option,
  Sekolah,
  Size,
  Wilayah,
} from "@/types";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  CalendarIcon,
  Camera,
  Contact2,
  FileText,
  MapPin,
  Ruler,
  User,
} from "lucide-react"; // Gunakan Lucide Icons
import React, { SetStateAction, useEffect, useRef, useState } from "react";

export default function Biodata({
  form,
  setForm,
  error,
  file,
  setFile,
  setFileBuktiPantiAsuhan,
}: {
  form: BiodataSate;
  setForm: React.Dispatch<SetStateAction<BiodataSate>>;
  error: ErrorType | null;
  file: File | null;
  setFile: React.Dispatch<SetStateAction<File | null>>;
  setFileBuktiPantiAsuhan: React.Dispatch<SetStateAction<File | null>>;
}) {
  const { user, isPending: isPendingUser } = useUser();
  const preview = useFilePreview(file);
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const uploadRef = useRef<HTMLInputElement | null>(null);
  const [openSize, setOpenSize] = useState(false);
  const [openProvinsi, setOpenProvinsi] = useState(false);
  const [openKabupaten, setOpenKabupaten] = useState(false);
  const [openKecamatan, setOpenKecamatan] = useState(false);
  const [openNegara, setOpenNegara] = useState(false);
  const [openAlatTransportasi, setOpenAlatTransportasi] = useState(false);
  const [openSekolah, setOpenSekolah] = useState(false);
  const [idFeederProvinsi, setIdFeederProvinsi] = useState("");
  const [idFeederKabupaten, setIdFeederKabupaten] = useState("");
  const [searchQuerySekolah, setSearchQuerySekolah] = useState("");
  const [searchQueryProvinsi, setSearchQueryProvinsi] = useState("");
  const [searchQueryKabupaten, setSearchQueryKabupaten] = useState("");
  const [searchQueryKecamatan, setSearchQueryKecamatan] = useState("");
  const [showSizeChart, setShowSizeChart] = useState(false);

  // --- QUERY HOOKS (Sama seperti sebelumnya, disembunyikan agar ringkas) ---
  const { data: dataProvinsi, isPending: isPendingProvinsi } = useQuery({
    queryKey: ["listProvinsi", form.provinsi, searchQueryProvinsi],
    queryFn: () =>
      getListWilayah(
        10,
        "1",
        undefined,
        searchQueryProvinsi ? undefined : form.provinsi,
        searchQueryProvinsi,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
        id_feeder: `${wilayah?.id_feeder}`,
      })),
  });

  const {
    data: dataKabupaten,
    refetch: refetchKabupaten,
    isPending: isPendingKabupaten,
  } = useQuery({
    queryKey: [
      "listKabupaten",
      idFeederProvinsi,
      form.kabupaten,
      searchQueryKabupaten,
    ],
    queryFn: () =>
      getListWilayah(
        10,
        undefined,
        idFeederProvinsi,
        searchQueryKabupaten ? undefined : form.kabupaten,
        searchQueryKabupaten,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
        id_feeder: `${wilayah.id_feeder}`,
      })),
    enabled: !!idFeederProvinsi,
  });

  const {
    data: dataKecamatan,
    refetch: refetchKecamatan,
    isPending: isPendingKecamatan,
  } = useQuery({
    queryKey: [
      "listKecamatan",
      idFeederKabupaten,
      searchQueryKecamatan ? undefined : form.kecamatan,
      searchQueryKecamatan,
    ],
    queryFn: () =>
      getListWilayah(
        10,
        undefined,
        idFeederKabupaten,
        form.kecamatan,
        searchQueryKecamatan,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
      })),
    enabled: !!idFeederKabupaten,
  });

  const { data: dataNegara } = useQuery({
    queryKey: ["listNegara"],
    queryFn: () => getListNegara(),
    select: (data) =>
      data?.map((negara: Negara) => ({
        label: negara.nama_negara,
        value: `${negara.id_ref_negara}`,
      })),
  });

  const { data: dataAgama } = useQuery({
    queryKey: ["listAgama"],
    queryFn: () => getListAgama(),
  });

  const { data: dataJenisTinggal } = useQuery({
    queryKey: ["listJenisTinggal"],
    queryFn: () => getListJenisTinggal(),
  });
  const selectedJenisTinggal =
    dataJenisTinggal &&
    dataJenisTinggal?.find((item: JenisTinggal) => {
      return item.id_ref_jenis_tinggal == form.id_ref_jenis_tinggal;
    });

  const { data: dataAlatTransportasi } = useQuery({
    queryKey: ["listAlatTransportasi"],
    queryFn: () => getListAlatTransportasi(),
    select: (data) =>
      data?.map((alatTransportasi: AlatTransportasi) => ({
        label: alatTransportasi.nama_alat_transportasi,
        value: `${alatTransportasi.id_ref_alat_transportasi}`,
      })),
  });

  const { data: dataSekolah, isPending: isPendingSekolah } = useQuery({
    queryKey: ["listSekolah", searchQuerySekolah],
    queryFn: () => getListSekolah("1", "10", searchQuerySekolah as string),
    select: (data) =>
      data?.data?.map((sekolah: Sekolah) => ({
        label: `${sekolah.nama_sekolah} (${sekolah.npsn_sekolah})`,
        value: `${sekolah.id_ref_sekolah}`,
      })),
  });

  const { data: dataSiswa, isPending: isPendingDataSiswa } =
    useQuery<DataSiswa>({
      queryKey: ["dataSiswaLengkap", user?.id, sessionPeriode?.id_sys_periode],
      queryFn: () => getDataSiswaLengkap(sessionPeriode?.id_sys_periode || ""),
      enabled: !!user?.id && !!sessionPeriode?.id_sys_periode,
    });
  const dataPendaftaran: DataPendaftaran | undefined =
    dataSiswa?.data_pendaftaran?.[0] || undefined;

  // --- EFFECT HOOKS ---
  useEffect(() => {
    if (form.provinsi) refetchKabupaten();
  }, [form.provinsi, refetchKabupaten]);

  useEffect(() => {
    if (form.kabupaten) refetchKecamatan();
  }, [form.kabupaten, refetchKecamatan]);

  useEffect(() => {
    if (!dataSiswa) return;

    const prodiLulus = dataPendaftaran?.data_pilihan_prodi?.find(
      (dataPilihanProdi: DataPilihanProdi) => {
        return dataPilihanProdi.is_lulus;
      },
    );
    const isPilihanLokasiFilled = prodiLulus?.pilihan_lokasi_kuliah !== null;
    const isBisaPilihLokasi =
      dataPendaftaran?.unitkerja?.kelas_kuliah &&
      dataPendaftaran?.unitkerja?.kelas_kuliah?.length > 1;

    setForm((prev) => ({
      ...prev,
      ...dataSiswa,
      id_ref_negara: dataSiswa?.id_ref_negara || "103",
      is_anak_tki: dataSiswa?.data_pendaftaran
        ? dataSiswa?.data_pendaftaran[0]?.is_anak_tki
        : false,
      is_daerah_bencana: dataSiswa?.data_pendaftaran
        ? dataSiswa?.data_pendaftaran[0]?.is_daerah_bencana
        : false,
      pilihan_lokasi_kuliah: isPilihanLokasiFilled
        ? (prodiLulus?.pilihan_lokasi_kuliah as LokasiKuliah)
        : isBisaPilihLokasi
          ? ""
          : ("singaraja" as LokasiKuliah),
      kontak_darurat_hubungan: dataSiswa?.kontak_darurat_hubungan,
    }));

    if (dataAgama) {
      setForm((prev) => ({ ...prev, id_ref_agama: dataSiswa?.id_ref_agama }));
    }

    if (dataJenisTinggal) {
      setForm((prev) => ({
        ...prev,
        id_ref_jenis_tinggal: dataSiswa?.id_ref_jenis_tinggal,
      }));
    }

    if (dataSiswa?.sekolah) {
      setSearchQuerySekolah(dataSiswa?.sekolah.npsn_sekolah);
      return;
    }
  }, [dataSiswa, dataPendaftaran, setForm, dataAgama, dataJenisTinggal]);

  useEffect(() => {
    const selectedProvinsi = dataProvinsi?.find(
      (provinsi: Option) => provinsi?.value == form.provinsi,
    );
    if (selectedProvinsi) setIdFeederProvinsi(selectedProvinsi?.id_feeder);

    const selectedKabupaten = dataKabupaten?.find(
      (kabupaten: Option) => kabupaten?.value == form.kabupaten,
    );
    if (selectedKabupaten) setIdFeederKabupaten(selectedKabupaten?.id_feeder);
  }, [
    setIdFeederProvinsi,
    setIdFeederKabupaten,
    dataProvinsi,
    dataKabupaten,
    form,
  ]);

  if (isPendingUser || isPendingDataSiswa) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
          Biodata Diri
        </h1>
        <section className="grid lg:grid-cols-2 grid-cols-1 gap-6">
          {Array(10)
            .fill(null)
            .map((_, index) => (
              <SkeletonRow key={index} />
            ))}
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500 flex-1">
      {/* --- HEADER --- */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          Biodata Diri
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
          Lengkapi data diri Anda dengan benar sesuai identitas resmi.
        </p>
      </div>

      {/* --- SECTION 1: FOTO & DATA AKUN --- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Foto Profil */}
        <Card className="lg:col-span-1 order-2 lg:order-1 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm h-fit">
          <CardHeader>
            <CardTitle className="text-lg">Foto Profil</CardTitle>
            <CardDescription>Upload pas foto terbaru formal.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <div
              className="relative group w-48 h-64 rounded-xl overflow-hidden border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              onClick={() => uploadRef.current?.click()}
            >
              {preview || form.path_foto ? (
                <img
                  src={preview || form.path_foto || ""}
                  alt="Foto Profil"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <Camera className="w-12 h-12 mb-2" />
                  <span className="text-sm font-medium">Upload Foto</span>
                </div>
              )}

              {/* Overlay Hover */}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-white text-sm font-medium">
                  Ganti Foto
                </span>
              </div>
            </div>

            <input
              ref={uploadRef}
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) setFile(e.target.files[0]);
              }}
            />

            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Badge variant="outline" className="text-xs">
                JPG/PNG
              </Badge>
              <Badge variant="outline" className="text-xs">
                Max 1MB
              </Badge>
            </div>
            <InputError text={error?.errors?.foto?.join(",")} />
          </CardContent>
        </Card>

        {/* Right Column: Data Readonly */}
        <Card className="lg:col-span-2 order-1 lg:order-2 border-blue-200 dark:border-blue-900/50 bg-blue-50/50 dark:bg-blue-950/20 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-blue-800 dark:text-blue-200">Data Pendaftaran</CardTitle>
            <CardDescription className="text-blue-600/80 dark:text-blue-300/80">
              Informasi ini diambil dari sistem dan tidak dapat diubah.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-blue-700 dark:text-blue-300">
                Nomor Pendaftaran
              </Label>
              <div className="p-2.5 font-mono text-xs font-medium bg-blue-100/50 dark:bg-blue-900/40 rounded-md text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800/50 cursor-not-allowed select-none">
                {dataPendaftaran?.nomor_pendaftaran || "-"}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                Program Studi Lulus
              </Label>
              <div className="p-2.5 font-sans text-sm font-bold bg-emerald-50 dark:bg-emerald-900/30 rounded-md text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800 cursor-not-allowed select-none ring-1 ring-emerald-200 dark:ring-emerald-800/50 shadow-sm">
                {dataPendaftaran?.unitkerja?.nama || "-"}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-blue-700 dark:text-blue-300">NIK / KTP</Label>
              <div className="p-2.5 font-mono text-xs font-medium bg-blue-100/50 dark:bg-blue-900/40 rounded-md text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800/50 cursor-not-allowed select-none">
                {dataSiswa?.nik || "-"}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-blue-700 dark:text-blue-300">NISN</Label>
              <div className="p-2.5 font-mono text-xs font-medium bg-blue-100/50 dark:bg-blue-900/40 rounded-md text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800/50 cursor-not-allowed select-none">
                {dataSiswa?.nisn || "-"}
              </div>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs text-blue-700 dark:text-blue-300">Email Terdaftar</Label>
              <div className="p-2.5 font-mono text-xs font-medium bg-blue-100/50 dark:bg-blue-900/40 rounded-md text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800/50 cursor-not-allowed select-none">
                {dataSiswa?.email || "-"}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* --- SECTION 2: FORM DATA DIRI --- */}
      <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5" /> Informasi Pribadi
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
          {/* Baris 1 */}
          <div className="space-y-2">
            <Label required>Nama Lengkap</Label>
            <Input
              required
              value={form.nama_lengkap || ""}
              onChange={(e) =>
                setForm({ ...form, nama_lengkap: e.target.value })
              }
              placeholder="Sesuai Ijazah/KTP"
            />
            <p className="text-[11px] text-slate-500">
              Gunakan Title Case (Contoh: Putu Setiawan).
            </p>
            <InputError text={error?.errors?.nama_lengkap?.join(",")} />
          </div>
          <div className="space-y-2">
            <Label required>Nama Ibu Kandung</Label>
            <Input
              required
              value={form.nama_ibu_kandung || ""}
              onChange={(e) =>
                setForm({ ...form, nama_ibu_kandung: e.target.value })
              }
              placeholder="Nama Ibu Kandung"
            />
            <InputError text={error?.errors?.nama_ibu_kandung?.join(",")} />
          </div>

          {/* Baris 2 */}
          <div className="space-y-2">
            <Label required>Tempat Lahir</Label>
            <Input
              required
              value={form.tempat_lahir || ""}
              onChange={(e) =>
                setForm({ ...form, tempat_lahir: e.target.value })
              }
              placeholder="Kota Tempat Lahir"
            />
            <InputError text={error?.errors?.tempat_lahir?.join(",")} />
          </div>
          <div className="space-y-2">
            <Label required>Tanggal Lahir</Label>
            <div className="relative">
              <Input
                required
                type="date"
                value={form.tanggal_lahir || ""}
                onChange={(e) =>
                  setForm({ ...form, tanggal_lahir: e.target.value })
                }
                className="pl-10"
              />
              <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>
            <p className="text-[11px] text-blue-600 dark:text-blue-400 font-medium mt-1">
              {form.tanggal_lahir &&
                formatDateIndonesia(form.tanggal_lahir, "medium")}
            </p>
            <InputError text={error?.errors?.tanggal_lahir?.join(",")} />
          </div>

          {/* Baris 3 */}
          <div className="space-y-2">
            <Label required>Jenis Kelamin</Label>
            <Select
              value={form.jenis_kelamin || ""}
              onValueChange={(v) => {
                if (!v) return;
                setForm({ ...form, jenis_kelamin: v as "L" | "P" });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pilih Jenis Kelamin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="L">Laki-laki</SelectItem>
                <SelectItem value="P">Perempuan</SelectItem>
              </SelectContent>
            </Select>
            <InputError text={error?.errors?.jenis_kelamin?.join(",")} />
          </div>
          <div className="space-y-2">
            <Label required>Agama</Label>
            <Select
              value={form.id_ref_agama ? String(form.id_ref_agama) : ""}
              onValueChange={(v) => {
                if (!v) return;
                setForm({ ...form, id_ref_agama: v });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pilih Agama" />
              </SelectTrigger>
              <SelectContent>
                {dataAgama?.map((a: Agama) => (
                  <SelectItem
                    key={a.id_ref_agama}
                    value={String(a.id_ref_agama)}
                  >
                    {a.nama_agama}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <InputError text={error?.errors?.id_ref_agama?.join(",")} />
          </div>

          {/* Baris 4 */}
          <div className="space-y-2">
            <Label required>No. Handphone (WhatsApp)</Label>
            <Input
              required
              maxLength={15}
              value={form.handphone || ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  handphone: e.target.value.replace(/\D/g, ""),
                })
              }
              placeholder="08xxxxxxxxxx"
              inputMode="numeric"
            />
            <InputError text={error?.errors?.handphone?.join(",")} />
          </div>
          <div className="space-y-2">
            <Label>NPWP (Opsional)</Label>
            <Input
              maxLength={15}
              value={form.npwp || ""}
              onChange={(e) =>
                setForm({ ...form, npwp: e.target.value.replace(/\D/g, "") })
              }
              placeholder="Nomor NPWP"
              inputMode="numeric"
            />
            <InputError text={error?.errors?.npwp?.join(",")} />
          </div>
        </CardContent>
      </Card>

      {/* --- SECTION 3: DATA FISIK & PENDUKUNG --- */}
      <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Ruler className="w-5 h-5" /> Data Fisik & Pendukung
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="space-y-2">
            <Label required>Tinggi Badan (cm)</Label>
            <Input
              type="number"
              value={form.tinggi_badan || ""}
              onChange={(e) =>
                setForm({ ...form, tinggi_badan: Number(e.target.value) })
              }
              placeholder="0"
            />
            <InputError text={error?.errors?.tinggi_badan?.join(",")} />
          </div>
          <div className="space-y-2">
            <Label required>Berat Badan (kg)</Label>
            <Input
              type="number"
              value={form.berat_badan || ""}
              onChange={(e) =>
                setForm({ ...form, berat_badan: Number(e.target.value) })
              }
              placeholder="0"
            />
            <InputError text={error?.errors?.berat_badan?.join(",")} />
          </div>
          <div className="space-y-2">
            <Label required className="flex items-center justify-between">
              Ukuran Baju
              <span
                className="text-xs text-blue-600 cursor-pointer hover:underline"
                onClick={() => setShowSizeChart(true)}
              >
                Lihat Size Chart
              </span>
            </Label>
            <Combobox
              open={openSize}
              setOpen={setOpenSize}
              value={form.ukuran_baju || ""}
              setValue={(v) => setForm({ ...form, ukuran_baju: v as Size })}
              options={listSizeOptions}
              placeholder="Pilih Ukuran"
            />
            <InputError text={error?.errors?.ukuran_baju?.join(",")} />
          </div>
          <div className="space-y-2">
            <Label required>Kewarganegaraan</Label>
            <Combobox
              open={openNegara}
              setOpen={setOpenNegara}
              value={form.id_ref_negara || ""}
              setValue={(v) => setForm({ ...form, id_ref_negara: v })}
              options={dataNegara}
              placeholder="Pilih Negara"
            />
            <InputError text={error?.errors?.id_ref_negara?.join(",")} />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label required>Sekolah Asal</Label>
            <Combobox
              serverSide
              open={openSekolah}
              setOpen={setOpenSekolah}
              value={form.id_ref_sekolah || ""}
              setValue={(v) => {
                setForm({ ...form, id_ref_sekolah: v });
              }}
              onChange={(e) => {
                setSearchQuerySekolah(e.target.value);
              }}
              options={dataSekolah}
              placeholder="Cari Sekolah (Nama / NPSN)"
              isPending={isPendingSekolah}
              searchQueryValue={searchQuerySekolah}
            />
            <InputError text={error?.errors?.id_ref_sekolah?.join(",")} />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label required>Alat Transportasi</Label>
            <p className="italic text-muted-foreground text-sm">
              Isi dengan alat transportasi yang akan digunakan selama kuliah
            </p>
            <Combobox
              open={openAlatTransportasi}
              setOpen={setOpenAlatTransportasi}
              value={form.id_ref_alat_transportasi || ""}
              setValue={(v) =>
                setForm({ ...form, id_ref_alat_transportasi: v })
              }
              options={dataAlatTransportasi}
              placeholder="Pilih Transportasi"
            />
            <InputError
              text={error?.errors?.id_ref_alat_transportasi?.join(",")}
            />
          </div>
        </CardContent>
      </Card>

      {/* --- SECTION 4: TEMPAT TINGGAL --- */}
      <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="w-5 h-5" /> Alamat Asal
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label required>Provinsi</Label>
              <Combobox
                serverSide
                open={openProvinsi}
                setOpen={setOpenProvinsi}
                value={form.provinsi || ""}
                setValue={(v) => {
                  setForm({
                    ...form,
                    provinsi: v,
                    kabupaten: "",
                    kecamatan: "",
                  });
                  const prov = dataProvinsi?.find((p: any) => p.value == v);
                  setIdFeederProvinsi(prov?.id_feeder);
                }}
                options={dataProvinsi}
                placeholder="Pilih Provinsi"
                onChange={(e) => setSearchQueryProvinsi(e.target.value)}
                isPending={isPendingProvinsi}
                searchQueryValue={searchQueryProvinsi}
              />
              <InputError text={error?.errors?.provinsi?.join(",")} />
            </div>

            <div className="space-y-2">
              <Label required>Kabupaten/Kota</Label>
              <Combobox
                serverSide
                open={openKabupaten}
                setOpen={setOpenKabupaten}
                value={form.kabupaten || ""}
                setValue={(v) => {
                  setForm({ ...form, kabupaten: v, kecamatan: "" });
                  const kab = dataKabupaten?.find((k: any) => k.value == v);
                  setIdFeederKabupaten(kab?.id_feeder);
                }}
                options={dataKabupaten}
                placeholder="Pilih Kabupaten"
                onChange={(e) => setSearchQueryKabupaten(e.target.value)}
                isPending={isPendingKabupaten}
                searchQueryValue={searchQueryKabupaten}
              />
              <InputError text={error?.errors?.kabupaten?.join(",")} />
            </div>

            <div className="space-y-2">
              <Label required>Kecamatan</Label>
              <Combobox
                serverSide
                open={openKecamatan}
                setOpen={setOpenKecamatan}
                value={form.kecamatan || ""}
                setValue={(v) => setForm({ ...form, kecamatan: v })}
                options={dataKecamatan}
                placeholder="Pilih Kecamatan"
                onChange={(e) => setSearchQueryKecamatan(e.target.value)}
                isPending={isPendingKecamatan}
                searchQueryValue={searchQueryKecamatan}
              />
              <InputError text={error?.errors?.kecamatan?.join(",")} />
            </div>

            <div className="space-y-2">
              <Label required>Desa/Kelurahan</Label>
              <Input
                value={form.kelurahan || ""}
                onChange={(e) =>
                  setForm({ ...form, kelurahan: e.target.value })
                }
                placeholder="Nama Desa/Kelurahan"
              />
              <InputError text={error?.errors?.kelurahan?.join(",")} />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label required>Jalan</Label>
              <Input
                value={form.jalan || ""}
                onChange={(e) => setForm({ ...form, jalan: e.target.value })}
                placeholder="Nama Jalan, No. Rumah, Gang, dll."
              />
              <InputError text={error?.errors?.jalan?.join(",")} />
            </div>

            <div className="grid md:grid-cols-3 grid-cols-1 gap-4 md:col-span-2">
              <div className="space-y-2">
                <Label required>Kode Pos</Label>
                <Input
                  maxLength={5}
                  value={form.kode_pos || ""}
                  onChange={(e) =>
                    setForm({ ...form, kode_pos: e.target.value })
                  }
                  placeholder="00000"
                />
                <InputError text={error?.errors?.kode_pos?.join(",")} />
              </div>
              <div className="space-y-2">
                <Label>RT</Label>
                <Input
                  type="number"
                  value={form.rt || ""}
                  onChange={(e) =>
                    setForm({ ...form, rt: Number(e.target.value) })
                  }
                  placeholder="00"
                />
              </div>
              <div className="space-y-2">
                <Label>RW</Label>
                <Input
                  type="number"
                  value={form.rw || ""}
                  onChange={(e) =>
                    setForm({ ...form, rw: Number(e.target.value) })
                  }
                  placeholder="00"
                />
              </div>
            </div>
          </div>

          <hr className="border-slate-200 dark:border-slate-800" />

          <div className="space-y-4">
            <div className="space-y-2">
              <Label required>Jenis Tinggal Saat Ini</Label>
              <Select
                value={
                  form.id_ref_jenis_tinggal
                    ? String(form.id_ref_jenis_tinggal)
                    : ""
                }
                onValueChange={(v) => {
                  if (!v) return;
                  setForm({ ...form, id_ref_jenis_tinggal: v });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pilih Jenis Tinggal" />
                </SelectTrigger>
                <SelectContent>
                  {dataJenisTinggal?.map((jt: JenisTinggal) => (
                    <SelectItem
                      key={jt.id_ref_jenis_tinggal}
                      value={String(jt.id_ref_jenis_tinggal)}
                    >
                      {jt.nama_jenis_tinggal}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <InputError
                text={error?.errors?.id_ref_jenis_tinggal?.join(",")}
              />
            </div>

            {selectedJenisTinggal?.nama_jenis_tinggal.startsWith("Panti") && (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 space-y-4">
                <div className="space-y-2">
                  <Label required>Nama Panti Asuhan</Label>
                  <Input
                    required
                    value={form.nama_panti_asuhan || ""}
                    onChange={(e) =>
                      setForm({ ...form, nama_panti_asuhan: e.target.value })
                    }
                    className="bg-white dark:bg-slate-900"
                  />
                  <InputError
                    text={error?.errors?.nama_panti_asuhan?.join(",")}
                  />
                </div>
                <div className="space-y-2">
                  <Label required>Upload Bukti Panti Asuhan</Label>
                  <Input
                    type="file"
                    className="bg-white dark:bg-slate-900"
                    onChange={(e) => {
                      if (e.target.files?.[0])
                        setFileBuktiPantiAsuhan(e.target.files[0]);
                    }}
                  />
                  <p className="text-xs text-slate-500">
                    Wajib upload surat keterangan resmi dari pengelola panti.
                  </p>
                  {dataSiswa?.path_bukti_panti_asuhan && (
                    <Badge
                      variant="outline"
                      className="cursor-pointer bg-white"
                      onClick={() =>
                        window.open(dataSiswa.path_bukti_panti_asuhan || "")
                      }
                    >
                      Lihat Berkas Terupload
                    </Badge>
                  )}
                  <InputError
                    text={error?.errors?.file_bukti_panti_asuhan?.join(",")}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* EMERGENCY CONTACT */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Contact2 className="w-5 h-5" /> Kontak Darurat
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
          <div className="md:col-span-2">
            <Label required>Hubungan Kontak Darurat</Label>
            <Select
              value={form.kontak_darurat_hubungan || ""}
              onValueChange={(value) => {
                if (!value) return;
                setForm((prev) => ({
                  ...prev,
                  kontak_darurat_hubungan: value as HubunganKeluargaEnum,
                }));
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pilih hubungan kontak darurat" />
              </SelectTrigger>
              <SelectContent>
                {listHubunganKeluarga?.map((option: Option, index: number) => (
                  <SelectItem key={index} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <InputError
              text={error?.errors?.kontak_darurat_hubungan?.join(",")}
            />
          </div>

          <div>
            <Label required>Nama Kontak Darurat</Label>
            <Input
              required
              placeholder="Nama kontak darurat..."
              value={form.kontak_darurat_nama || ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  kontak_darurat_nama: e.target.value,
                }))
              }
            />
            <InputError text={error?.errors?.kontak_darurat_nama?.join(",")} />
          </div>

          <div>
            <Label required>Nomor Handphone Kontak Darurat</Label>
            <Input
              required
              maxLength={15}
              value={form.kontak_darurat_hp || ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  kontak_darurat_hp: e.target.value.replace(/\D/g, ""),
                })
              }
              placeholder="08xxxxxxxxxx"
              inputMode="numeric"
            />
            <InputError text={error?.errors?.kontak_darurat_hp?.join(",")} />
          </div>
        </CardContent>
      </Card>

      {/* --- SECTION 5: KONDISI KHUSUS --- */}
      <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertCircle className="w-5 h-5" /> Kondisi & Kebutuhan Khusus
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Minat Lokasi (Jika Ada) */}
          {dataPendaftaran?.unitkerja?.kelas_kuliah &&
            dataPendaftaran.unitkerja.kelas_kuliah.length > 1 && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 space-y-3">
                <div className="space-y-2">
                  <Label required>Minat Lokasi Kuliah</Label>
                  <Select
                    value={form.pilihan_lokasi_kuliah || ""}
                    onValueChange={(v) => {
                      if (!v) return;
                      setForm({
                        ...form,
                        pilihan_lokasi_kuliah: v as LokasiKuliah,
                      });
                    }}
                  >
                    <SelectTrigger className="bg-white dark:bg-slate-900">
                      <SelectValue placeholder="Pilih Lokasi" />
                    </SelectTrigger>
                    <SelectContent>
                      {dataPendaftaran.unitkerja.kelas_kuliah.map((v, i) => (
                        <SelectItem key={i} value={v.toLowerCase()}>
                          {v.charAt(0).toUpperCase() + v.slice(1).toLowerCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                  <strong>Catatan:</strong> Lokasi final ditentukan berdasarkan
                  kapasitas kelas dan domisili KTP. Pilihan ini hanya sebagai
                  preferensi.
                </p>
              </div>
            )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>1. Apakah Anda anak TKI di perbatasan NKRI?</Label>
              <div className="flex gap-4 mt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="tki-ya"
                    checked={!!form.is_anak_tki}
                    onCheckedChange={() =>
                      setForm({ ...form, is_anak_tki: true })
                    }
                  />
                  <label htmlFor="tki-ya" className="text-sm">
                    Ya
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="tki-tidak"
                    checked={!form.is_anak_tki}
                    onCheckedChange={() =>
                      setForm({ ...form, is_anak_tki: false })
                    }
                  />
                  <label htmlFor="tki-tidak" className="text-sm">
                    Tidak
                  </label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                2. Apakah wilayah tempat tinggal mengalami bencana/konflik
                berat?
              </Label>
              <div className="flex gap-4 mt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="bencana-ya"
                    checked={!!form.is_daerah_bencana}
                    onCheckedChange={() =>
                      setForm({ ...form, is_daerah_bencana: true })
                    }
                  />
                  <label htmlFor="bencana-ya" className="text-sm">
                    Ya
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="bencana-tidak"
                    checked={!form.is_daerah_bencana}
                    onCheckedChange={() =>
                      setForm({ ...form, is_daerah_bencana: false })
                    }
                  />
                  <label htmlFor="bencana-tidak" className="text-sm">
                    Tidak
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Kebutuhan Khusus</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4 border border-slate-200 dark:border-slate-800 rounded-lg">
              {listKebutuhanKhusus.map((value, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Checkbox
                    id={`kk-${index}`}
                    checked={form?.kebutuhan_khusus?.includes(value) || false}
                    onCheckedChange={(isChecked) => {
                      setForm((prev) => {
                        const updated = isChecked
                          ? [...(prev.kebutuhan_khusus || []), value]
                          : (prev.kebutuhan_khusus || []).filter(
                            (item) => item !== value,
                          );
                        return { ...prev, kebutuhan_khusus: updated };
                      });
                    }}
                  />
                  <label
                    htmlFor={`kk-${index}`}
                    className="text-sm cursor-pointer"
                  >
                    {value}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* --- ERROR SUMMARY --- */}
      {error && error?.errors && (
        <Alert
          variant="destructive"
          className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300"
        >
          <AlertTitle>Terjadi Kesalahan</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4 mt-2 space-y-1 text-sm">
              {Object.keys(error.errors).map((key, i) => (
                <li key={i}>{error.errors[key]}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* --- MODAL SIZE CHART --- */}
      <Modal
        show={showSizeChart}
        onClose={() => setShowSizeChart(false)}
        className="w-full max-w-lg"
      >
        <div className="p-4">
          <img
            src="/size-chart.jpeg"
            alt="Size Chart"
            className="w-full h-auto rounded-lg"
          />
        </div>
      </Modal>
    </div>
  );
}
